// MARCA | 12/05/2026 | Upsell coordinator – batches parent + upsell items into a single /cart/add.js request
// MARCA | 14/05/2026 | Rewritten to intercept form submission (capture phase) instead of deferred second request
import { CartAddEvent } from '@theme/events';

/** @typedef {{ variantId: number, productTitle: string }} GiftboxPending */
/** @typedef {{ variantId: number, productTitle: string, message: string }} GiftcardPending */

/** @type {GiftboxPending | null} */
var pendingGiftbox = null;
/** @type {GiftcardPending | null} */
var pendingGiftcard = null;
var batchAddInProgress = false;

// MARCA | 12/05/2026 | Unique group ID binds each parent + upsell bundle into a single cart group
/** @type {string | null} */
var groupId = null;

// MARCA | 12/05/2026 | Track selected upsells via custom events dispatched by the upsell blocks
document.addEventListener('upsell:toggle', function (e) {
  var d = /** @type {CustomEvent} */ (e).detail;
  if (!d) return;

  if (d.type === 'giftbox') {
    pendingGiftbox = d.selected
      ? { variantId: d.variantId, productTitle: d.productTitle }
      : null;
  }

  if (d.type === 'giftcard') {
    pendingGiftcard = d.selected
      ? { variantId: d.variantId, productTitle: d.productTitle, message: d.message }
      : null;
  }

  updateGroupInput();
});

// MARCA | 13/05/2026 | Toggle the static hidden _upsell_group input that lives in buy-buttons.liquid.
// Queried fresh each time so it survives section re-renders / DOM morphs.
function updateGroupInput() {
  var hasUpsells = !!(pendingGiftbox || pendingGiftcard);
  var input = /** @type {HTMLInputElement | null} */ (
    document.querySelector('[data-upsell-group-input]')
  );

  if (!input) return;

  if (hasUpsells) {
    if (!groupId) {
      groupId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    }
    input.disabled = false;
    input.value = groupId;
  } else {
    groupId = null;
    input.disabled = true;
    input.value = '';
  }
}

// MARCA | 14/05/2026 | Capture-phase click interception on the add-to-cart button.
// When upsells are pending, prevents the click from reaching the theme's handlers entirely,
// so no form submit event is dispatched. Batches parent + upsells into a single /cart/add.js.
// When no upsells are selected, the click passes through untouched to the theme's normal flow.
document.addEventListener(
  "click",
  function (e) {
    var target = /** @type {HTMLElement} */ (e.target);
    var submitButton = /** @type {HTMLButtonElement | null} */ (
      target.closest('button[type="submit"][name="add"]')
    );
    if (!submitButton) return;

    var productFormComponent = /** @type {HTMLElement | null} */ (
      submitButton.closest("product-form-component")
    );
    if (!productFormComponent) return;

    if (!pendingGiftbox && !pendingGiftcard) return;
    if (batchAddInProgress) return;
    if (submitButton.disabled) return;

    e.stopImmediatePropagation();
    e.preventDefault();

    batchAddInProgress = true;

    var form = /** @type {HTMLFormElement | null} */ (
      productFormComponent.querySelector("form")
    );
    var variantIdInput = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('input[name="id"]') || null
    );
    var variantId = parseInt(variantIdInput?.value || "0", 10);
    var qtyInput = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('input[name="quantity"]') || null
    );
    var quantity = qtyInput ? parseInt(qtyInput.value || "1", 10) || 1 : 1;
    var currentGroup = groupId;
    var routes = /** @type {any} */ (window.Shopify)?.routes?.root || "/";

    /** @type {Array<{id: number, quantity: number, properties: Record<string, string>}>} */
    var items = [];

    /** @type {Record<string, string>} */
    var parentProps = {};
    if (currentGroup) parentProps["_upsell_group"] = currentGroup;
    items.push({ id: variantId, quantity: quantity, properties: parentProps });

    if (pendingGiftbox) {
      /** @type {Record<string, string>} */
      var gbProps = { "Gift wrap for": pendingGiftbox.productTitle };
      if (currentGroup) gbProps["_upsell_group"] = currentGroup;
      items.push({
        id: pendingGiftbox.variantId,
        quantity: 1,
        properties: gbProps,
      });
    }

    if (pendingGiftcard) {
      /** @type {Record<string, string>} */
      var gcProps = {
        "Gift card for": pendingGiftcard.productTitle,
        Message: pendingGiftcard.message,
      };
      if (currentGroup) gcProps["_upsell_group"] = currentGroup;
      items.push({
        id: pendingGiftcard.variantId,
        quantity: 1,
        properties: gcProps,
      });
    }

    fetch(routes + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("Batch add failed: " + res.status);
        return fetch(routes + "cart.js");
      })
      .then(function (res) {
        return res.json();
      })
      .then(function (cartData) {
        var pfc = /** @type {HTMLElement} */ (productFormComponent);
        pfc.dispatchEvent(
          new CartAddEvent(cartData, String(variantId), {
            source: "product-form-component",
            itemCount: cartData.item_count,
            productId: pfc.dataset.productId,
          }),
        );

        pendingGiftbox = null;
        pendingGiftcard = null;
        groupId = null;
        var resetInput = /** @type {HTMLInputElement | null} */ (
          document.querySelector("[data-upsell-group-input]")
        );
        if (resetInput) {
          resetInput.disabled = true;
          resetInput.value = "";
        }

        document.dispatchEvent(new CustomEvent("upsell:reset"));
      })
      .catch(function (err) {
        console.error("[UpsellCoordinator] Batch add failed:", err);
      })
      .finally(function () {
        batchAddInProgress = false;
      });
  },
  true,
);
